import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiMessageCircle, FiSearch, FiSliders, FiExternalLink, FiMoreHorizontal, FiArrowRight } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { CommunicationsQuestionsResult, InvestorQuestion } from '../../../model/CommunicationsQuestionsResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { toast } from '../Toast';
import { initials } from '../Header';

type Tab = 'open' | 'answered' | 'published';
const inTab = (q: InvestorQuestion, tab: Tab) => tab === 'open' ? !q.answered : q.answered && q.isPublic === (tab === 'published');

const ago = (utc: string) => {
    const minutes = moment().diff(moment(utc), 'minutes');
    if (minutes < 60) return `${Math.max(minutes, 1)} min ago`;
    if (minutes < 60 * 24) return `${Math.floor(minutes / 60)} h ago`;
    if (minutes < 60 * 24 * 30) return `${Math.floor(minutes / 60 / 24)} d ago`;
    return moment(utc).format('D MMM YYYY');
};
const hoursOld = (q: InvestorQuestion) => moment().diff(moment(q.created), 'hours');
const shares = (q: InvestorQuestion) => `${(q.shares || 0).toLocaleString()} shares`;
const who = (q: InvestorQuestion) => q.shares ? `Shareholder · ${shares(q)}` : q.signedUp ? 'Signed-up user · not on registry' : 'Not signed up · not on registry';

const host = (url: string | null) => {
    try { return new URL(url || '').host.replace(/^www\./, ''); } catch { return null; }
};

interface Props {
    onChanged?: () => void; // refetch the sub-tab badge counts
}

// spec 2:71455. Assign omitted (see outstanding.md); Question Settings, Published Answers and ⋯ are dead until their screens are built.
const InvestorQuestions: React.FC<Props> = ({ onChanged }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [result, setResult] = useState<CommunicationsQuestionsResult | null>(null);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const [tab, setTab] = useState<Tab>('open');
    const [query, setQuery] = useState('');
    const { id: linkedGuid } = useParams<{ id?: string }>(); // moderator email link
    const [selectedGuid, setSelectedGuid] = useState<string | null>(linkedGuid ?? null);
    const [answer, setAnswer] = useState('');
    const [similar, setSimilar] = useState<string[]>([]);
    const [busy, setBusy] = useState<'' | 'draft' | 'ai' | 'private' | 'publish'>('');

    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        const [err, data] = await to(api().getCommunicationsQuestions(stock.id));
        if (!isMounted()) return;
        if (err) setError(err.response?.status === 403 ? 'You need the Reply To Comment role to answer investor questions.' : err.message || 'Failed to load questions');
        else {
            setResult(data);
            const linked = data.questions.find((x: InvestorQuestion) => x.guid === linkedGuid);
            if (linked && reload === 0) setTab((['open', 'answered', 'published'] as Tab[]).find(t => inTab(linked, t)) ?? 'open');
        }
    }, [stock?.id, reload]);

    const questions = result?.questions ?? [];
    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return questions.filter(x => inTab(x, tab) && (!q || `${x.name} ${x.email} ${x.comment}`.toLowerCase().includes(q)));
    }, [questions, tab, query]);
    const selected = visible.find(x => x.guid === selectedGuid) ?? visible[0] ?? null;

    useEffect(() => {
        setAnswer(selected?.draft ?? selected?.response ?? '');
        setSimilar([]);
        if (!stock?.id || !selected) return;
        let live = true;
        api().getSimilarQuestions(stock.id, selected.guid).then(guids => { if (live) setSimilar(guids); }).catch(() => undefined);
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stock?.id, selected?.guid]);

    const similarQuestions = similar.map(g => questions.find(x => x.guid === g)).filter((x): x is InvestorQuestion => !!x?.response);

    const saveDraft = async () => {
        if (!stock?.id || !selected) return;
        setBusy('draft');
        const [err] = await to(api().saveQuestionDraft(stock.id, selected.guid, answer));
        setBusy('');
        if (err) { toast.error('Failed to save the draft'); return; }
        toast.success('Draft saved');
        setResult(r => r && { ...r, questions: r.questions.map(x => x.guid === selected.guid ? { ...x, draft: answer.trim() ? answer : null } : x) });
    };

    const draftWithAi = async () => {
        if (!stock?.id || !selected) return;
        setBusy('ai');
        const [err, text] = await to(api().aiDraftQuestionAnswer(stock.id, selected.guid));
        setBusy('');
        if (err || !text) { toast.error('Could not draft an answer'); return; }
        setAnswer(text);
    };

    const send = async (isPublic: boolean) => {
        if (!selected || !answer.trim()) return;
        setBusy(isPublic ? 'publish' : 'private');
        const [err, res] = await to(api().postAnnouncementCommentResponseRequest({ guid: selected.guid, comment: answer, isPublic }));
        setBusy('');
        if (err || !res?.success) { toast.error(res?.errorMessage || 'Failed to send the answer'); return; }
        toast.success(isPublic ? 'Answer published' : 'Answer sent privately');
        setReload(r => r + 1);
        onChanged?.();
    };

    const count = (t: Tab) => questions.filter(x => inTab(x, t)).length;
    const tabButton = (t: Tab, label: string) =>
        <button type="button" className={tab === t ? 'active' : ''} onClick={() => { setTab(t); setSelectedGuid(null); }}>{label} · {count(t)}</button>;

    return <div className="investor-questions">
        <div className="page-header">
            <div>
                <h1>Investor Questions</h1>
                <p>Questions asked on {host(result?.websiteUrl ?? null) || stock?.companyName || 'your website'}, and the answers you publish</p>
            </div>
            <div className="actions">
                <button type="button" className="btn-outline"><FiSliders /> Question Settings</button>
                <button type="button" className="btn-outline"><FiExternalLink /> Published Answers</button>
            </div>
        </div>

        <div className="callout">
            <FiMessageCircle />
            <div>
                <div className="t">Every question is answered here</div>
                <div className="d">Questions asked on an announcement, on the Website Hub or through the contact form all land in this list. Answering once publishes the answer for every shareholder, so nothing gets two different replies.</div>
            </div>
        </div>

        {error && <p className="error">{error}</p>}
        {!error && !result && <p className="loading">Loading questions…</p>}

        {result && <div className="panes">
            <div className="list card">
                <div className="seg">
                    {tabButton('open', 'Open')}
                    {tabButton('answered', 'Answered')}
                    {tabButton('published', 'Published')}
                </div>
                <label className="search"><FiSearch /><input placeholder="Search questions…" value={query} onChange={e => { setQuery(e.target.value); setSelectedGuid(null); }} /></label>
                <ul>
                    {visible.map(q => <li key={q.guid} className={q === selected ? 'active' : ''} onClick={() => setSelectedGuid(q.guid)}>
                        <span className="mark">{initials(q.name || '')}</span>
                        <div className="body">
                            <div className="top"><span className="name">{q.name}</span><span className="when">{ago(q.created)}</span></div>
                            <div className="snippet">{q.comment}</div>
                            <div className="meta">
                                {who(q)}
                                {!q.answered && hoursOld(q) < 24 && <span className="tag new">New</span>}
                                {!q.answered && hoursOld(q) >= 48 && <span className="tag late">48h+</span>}
                            </div>
                        </div>
                    </li>)}
                </ul>
                {visible.length === 0 && <p className="empty">{query ? 'No questions match your search.' : `No ${tab} questions.`}</p>}
            </div>

            {selected && <div className="detail card">
                <div className="asker">
                    <span className="mark">{initials(selected.name || '')}</span>
                    <div className="who">
                        <div className="line">
                            <span className="name">{selected.name}</span>
                            {!!selected.shares && <span className="pill ok">Verified holder</span>}
                            {!selected.shares && <span className="pill">{selected.signedUp ? 'Signed-up user' : 'Not signed up'}</span>}
                            {!!selected.shares && <span className="pill">{shares(selected)}{selected.rank && selected.rank <= 100 ? ' · Top 100' : ''}</span>}
                        </div>
                        <div className="sub">{selected.email} · Asked {ago(selected.created)}
                            {selected.locationHref && <> from <a href={selected.locationHref} target="_blank" rel="noopener noreferrer">{selected.locationHref.replace(/^https?:\/\//, '')}</a></>}</div>
                    </div>
                    <button type="button" className="btn-outline icon" aria-label="More actions"><FiMoreHorizontal /></button>
                </div>

                <div className="question">
                    <div className="label">Question</div>
                    <p>{selected.comment}</p>
                    {selected.announcementHeadline && <div className="on">On “{selected.announcementHeadline}”</div>}
                </div>

                {similarQuestions.length > 0 && <div className="similar">
                    <div className="h">
                        <span>Similar answered questions</span>
                        <button type="button" className="link" onClick={() => setAnswer(similarQuestions[0].response || '')}>Insert Answer <FiArrowRight /></button>
                    </div>
                    {similarQuestions.map(s => <button type="button" key={s.guid} className="row" title="Insert this answer" onClick={() => setAnswer(s.response || '')}>
                        <span>“{s.comment}” — answered {moment(s.responseDate || s.created).format('D MMM')}{s.isPublic ? ', published' : ''}</span>
                        {s.isPublic && <span className="pill info">Public</span>}
                    </button>)}
                </div>}

                <div className="answer">
                    <div className="h">
                        <span>Your answer</span>
                        <button type="button" className="link" disabled={!!busy} onClick={draftWithAi}>✦ {busy === 'ai' ? 'Drafting…' : 'Draft with AI'}</button>
                    </div>
                    <textarea value={answer} onChange={e => setAnswer(e.target.value)} placeholder={`Write your answer to ${selected.name}…`} />
                    <div className="foot">
                        <button type="button" className="text" disabled={!!busy} onClick={saveDraft}>{busy === 'draft' ? 'Saving…' : 'Save Draft'}</button>
                        <button type="button" className="btn-outline" disabled={!!busy || !answer.trim()} onClick={() => send(false)}>
                            {busy === 'private' ? 'Sending…' : 'Answer Privately'}</button>
                        <button type="button" className="btn-primary" disabled={!!busy || !answer.trim() || selected.privateRequested}
                            title={selected.privateRequested ? 'The investor asked for a private reply' : undefined} onClick={() => send(true)}>
                            {busy === 'publish' ? 'Publishing…' : 'Answer & Publish'}</button>
                    </div>
                </div>
            </div>}
        </div>}
    </div>;
};

export default InvestorQuestions;
